import { FileMimetype, acceptedProductAuthoritiesDocumentMimetypes } from 'constants/misc'
import { MessageThreadDTO, MessageThreadState } from 'models/messageThreads'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useGetAssignmentMessageThreads, useSendAssignmentMessageThread } from 'dataQueries/messageThreads'

import { AssignmentDocumentsEndpoint } from 'constants/API'
import { DocumentUploadType } from 'constants/documents'
import { Options } from 'components/common/FileAPI'
import constate from 'constate'
import { getSignedURLRetrieverSeq } from 'components/common/FileAPI/fileUtils'
import { useAuth0 } from 'utils/auth'
import { useGalleryProduct } from 'components/pages/Gallery/_main/contexts'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

export const [ThreadContextProvider, useThreads] = constate(({
  assignmentId,
}: {
  assignmentId: string
}) => {

  const { spawnErrorToast } = useSnackbar()
  const { t } = useTranslation(['threads'])
  const { roles } = useAuth0()

  const { userPropertyWorkspaceId } = useUserData()
  const { isEnergyCertificateProduct, isAuthoritiesDocumentsProduct } = useGalleryProduct()

  const [headerButtonText, setHeaderButtonText] = useState<string | null>(null)
  const [requestThreadTitle, setRequestThreadTitle] = useState<string>('')
  const [requestThreadStatus, setRequestThreadStatus] = useState<MessageThreadState | null>(null)
  const [requestThreadStartedDate, setRequestThreadStartedDate] = useState<string>('')
  const [requestThreadId, setRequestThreadId] = useState<string>('')
  const [allowUploadOfDocuments, setAllowUploadOfDocuments] = useState<boolean>(true)
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false)
  const [showParticipantName, setShowParticipantName] = useState<boolean>(false)
  const [acceptedMimeTypes, setAcceptedMimeTypes] = useState<Record<FileMimetype, string[]>>(acceptedProductAuthoritiesDocumentMimetypes) // Default accepted MIME types for DocEstate
  const [isCreateRequestDrawerOpen, setIsCreateRequestDrawerOpen] = useState(false)
  const [selectedThread, setSelectedThread] = useState<MessageThreadDTO | null>(null)

  const sendAssignmentMessageThread = useSendAssignmentMessageThread()
  const currentAssignmentThreads = useGetAssignmentMessageThreads(assignmentId)

  const assignmentThreadsData = useMemo(() => currentAssignmentThreads.data?.data, [currentAssignmentThreads?.data])
  const allowCreateRequestEnergyCertificate = useMemo(() => isEnergyCertificateProduct && assignmentThreadsData?.length === 0, [isEnergyCertificateProduct, assignmentThreadsData])

  const sortThreads = useCallback((threads: MessageThreadDTO[]) => {
    return threads.sort((a, b) => {
      // First sort by state: OPEN threads
      if (a.state === 'OPEN' && b.state !== 'OPEN') return -1
      if (a.state !== 'OPEN' && b.state === 'OPEN') return 1

      // Then sort by creation date (newest first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
  }, [])

  /** List of thread messages sorted by state and creation date. */
  const threadList = useMemo(() => {
    if (!assignmentThreadsData || assignmentThreadsData.length === 0) return []

    return sortThreads(assignmentThreadsData)
  }, [assignmentThreadsData, sortThreads])

  useEffect(() => {
    const updateSelectedThread = threadList.find(thread => thread.id === selectedThread?.id) || null
    setSelectedThread(updateSelectedThread)
  }, [selectedThread, threadList])

  const fileAPIOptions: Options = {
    uploadUrlResolver: getSignedURLRetrieverSeq((fileEntry, api) => {
      return api.post(
        roles.isClient ? AssignmentDocumentsEndpoint.POST_UPLOAD_URL_WITH_WORKSPACE : AssignmentDocumentsEndpoint.POST_UPLOAD_URL,
        { assignmentId, workspaceId: userPropertyWorkspaceId },
        {
          filename: fileEntry.fileObject.name,
          contentType: fileEntry.fileObject.type,
          type: DocumentUploadType.INPUT,
        },
        true
      )
    }),
    deleteHandler: (fileEntry, api) => api.delete(
      roles.isClient ? AssignmentDocumentsEndpoint.DELETE_DOCUMENT_WITH_WORKSPACE : AssignmentDocumentsEndpoint.DELETE_DOCUMENT,
      { assignmentId, workspaceId: userPropertyWorkspaceId },
      true,
      {
        params: {
          type: DocumentUploadType.INPUT,
          filename: fileEntry.gcFilename,
        }
      }
    )
  }

  /** Handles the submission of a message with attached files to a specific thread within an assignment. */
  const onSubmitMessage = useCallback((assignmentId: string, threadId: string, content: string, attachmentFilenames: string[]) => {
    sendAssignmentMessageThread.mutate(
      { assignmentId, threadId, content, attachmentFilenames },
      {
        onError: (error) => {
          spawnErrorToast(
            t('error_message'),
            { error: error.request }
          )
        }
      }
    )
  }, [sendAssignmentMessageThread, spawnErrorToast, t])

  const handleOnCreateRequestThread = useCallback(() => {
    if (allowCreateRequestEnergyCertificate || isAuthoritiesDocumentsProduct) {
      setIsCreateRequestDrawerOpen(true)
    }
  }, [allowCreateRequestEnergyCertificate, isAuthoritiesDocumentsProduct])

  const handleCloseCreateRequestDrawer = useCallback((refetchThreads?: boolean) => {
    setIsCreateRequestDrawerOpen(false)
    if (refetchThreads) currentAssignmentThreads.refetch()
  }, [currentAssignmentThreads])

  return {
    headerButtonText,
    requestThreadTitle,
    requestThreadStatus,
    requestThreadStartedDate,
    requestThreadId,
    sendAssignmentMessageThread,
    allowUploadOfDocuments,
    isPreviewMode,
    showParticipantName,
    acceptedMimeTypes,
    setAcceptedMimeTypes,
    setShowParticipantName,
    setIsPreviewMode,
    setAllowUploadOfDocuments,
    setRequestThreadTitle,
    setRequestThreadId,
    setRequestThreadStartedDate,
    setRequestThreadStatus,
    onSubmitMessage,
    setHeaderButtonText,
    assignmentThreadsData,
    currentAssignmentThreads,
    threadList,
    fileAPIOptions,
    allowCreateRequestEnergyCertificate,
    isCreateRequestDrawerOpen,
    setIsCreateRequestDrawerOpen,
    handleOnCreateRequestThread,
    handleCloseCreateRequestDrawer,
    selectedThread,
    setSelectedThread
  }
})
